/**
 * Error boundary components with Panerai dark theme
 * Catches JavaScript errors and displays fallback UI
 */

import { Component } from 'react'
import { Button } from './Button'

/**
 * Class-based Error Boundary (React requirement)
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo })

    // Log to console in development
    console.error('ErrorBoundary caught an error:', error, errorInfo)

    // Call optional onError callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null })
    if (this.props.onRetry) {
      this.props.onRetry()
    }
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback component
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Render custom fallback render prop
      if (this.props.fallbackRender) {
        return this.props.fallbackRender({
          error: this.state.error,
          errorInfo: this.state.errorInfo,
          retry: this.handleRetry,
        })
      }

      // Default error UI
      return (
        <ErrorFallback
          error={this.state.error}
          onRetry={this.handleRetry}
          minimal={this.props.minimal}
        />
      )
    }

    return this.props.children
  }
}

/**
 * Default error fallback UI
 */
export function ErrorFallback({
  error,
  onRetry,
  minimal = false,
  title = 'Something went wrong',
  message = 'We encountered an unexpected error. Please try again.',
}) {
  if (minimal) {
    return (
      <div className="p-4 text-center">
        <p className="text-indices-muted mb-2">{message}</p>
        {onRetry && (
          <Button variant="secondary" size="sm" onClick={onRetry}>
            Try again
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className="min-h-[300px] flex flex-col items-center justify-center p-8 text-center">
      <div className="w-16 h-16 bg-status-error/20 rounded-full flex items-center justify-center mb-4">
        <svg
          className="w-8 h-8 text-status-error"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      </div>

      <h3 className="text-lg font-semibold text-indices-bright mb-2 font-display">{title}</h3>
      <p className="text-indices-muted mb-6 max-w-sm">{message}</p>

      {error && import.meta.env.DEV && (
        <div className="mb-6 p-4 bg-dial-surface border border-dial-border rounded-lg max-w-md overflow-auto text-left">
          <p className="text-xs text-indices-muted mb-1">Error details (dev only):</p>
          <pre className="text-xs text-status-error whitespace-pre-wrap">
            {error.toString()}
          </pre>
        </div>
      )}

      <div className="flex gap-3">
        {onRetry && (
          <Button onClick={onRetry}>Try again</Button>
        )}
        <Button
          variant="secondary"
          onClick={() => window.location.reload()}
        >
          Reload page
        </Button>
      </div>
    </div>
  )
}

/**
 * Error message component for inline errors
 */
export function ErrorMessage({
  error,
  onRetry,
  className = '',
}) {
  if (!error) return null

  const errorMessage = typeof error === 'string'
    ? error
    : error?.message || 'An unexpected error occurred'

  return (
    <div className={`bg-status-error/10 border border-status-error/30 rounded-xl p-4 animate-fade-in ${className}`}>
      <div className="flex items-start gap-3">
        <svg
          className="w-5 h-5 text-status-error flex-shrink-0 mt-0.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <div className="flex-1">
          <p className="text-sm text-status-error leading-relaxed">{errorMessage}</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="mt-2 text-sm text-status-error hover:text-status-error/80 font-medium transition-colors"
            >
              Try again
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Network error component
 */
export function NetworkError({ onRetry, className = '' }) {
  return (
    <div className={`text-center py-8 ${className}`}>
      <div className="w-16 h-16 bg-dial-elevated rounded-full flex items-center justify-center mx-auto mb-4">
        <svg
          className="w-8 h-8 text-indices-muted"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3"
          />
        </svg>
      </div>
      <h3 className="text-lg font-medium text-indices-bright mb-2 font-display">Connection lost</h3>
      <p className="text-indices-muted mb-4">
        Please check your internet connection and try again.
      </p>
      {onRetry && (
        <Button onClick={onRetry} variant="secondary">
          Retry
        </Button>
      )}
    </div>
  )
}

/**
 * Not found error component
 */
export function NotFoundError({
  title = 'Not found',
  message = "The page you're looking for doesn't exist.",
  onBack,
  className = '',
}) {
  return (
    <div className={`text-center py-12 ${className}`}>
      <div className="text-6xl font-bold text-dial-elevated mb-4 font-display">404</div>
      <h3 className="text-xl font-semibold text-indices-bright mb-2 font-display">{title}</h3>
      <p className="text-indices-muted mb-6">{message}</p>
      {onBack && (
        <Button onClick={onBack}>Go back</Button>
      )}
    </div>
  )
}

/**
 * Empty state component (not technically an error, but related)
 */
export function EmptyState({
  icon,
  title,
  message,
  action,
  className = '',
}) {
  return (
    <div className={`text-center py-8 animate-fade-in ${className}`}>
      {icon && (
        <div className="w-16 h-16 bg-dial-elevated rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-indices-muted">{icon}</span>
        </div>
      )}
      <h3 className="text-lg font-medium text-indices-bright mb-2 font-display">{title}</h3>
      {message && <p className="text-indices-muted mb-4 leading-relaxed">{message}</p>}
      {action}
    </div>
  )
}

/**
 * Query error handler - for React Query errors
 */
export function QueryErrorHandler({ error, refetch, isRefetching }) {
  const isNetworkError = error?.message?.toLowerCase().includes('network') ||
    error?.message?.toLowerCase().includes('fetch')

  if (isNetworkError) {
    return <NetworkError onRetry={refetch} />
  }

  return (
    <ErrorMessage
      error={error}
      onRetry={isRefetching ? undefined : refetch}
    />
  )
}

export default ErrorBoundary
